import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Minus,
  Plus,
  ChevronDown,
  Search,
  Calendar,
  Clock,
} from "lucide-react";
import { motion } from "framer-motion";
import DateCalendar from "./DateCalendar";
import TimeSpinner from "./ui/TimeSpinner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { format, parseISO, isValid, parse } from "date-fns";
import { cn } from "../lib/utils";
import GlobalLoader from "./ui/GlobalLoader";
import { Booking, BookingStatus } from "../services/BookingsApi";
import { useAuth } from "../auth/AuthContext";
import { getBusinessServices } from "../services/businessServicesApi";
import { getAdminProducts } from "../services/productsApi";
import { BusinessService } from "../types/businessService";
import { Product } from "../types/product";

interface EmployeeOption {
  id: number;
  name: string;
}

interface LineItem {
  id: string;
  name: string;
  unitPrice: number;
  quantity: number;
  type: string;
  serviceId?: string;
  productId?: string;
}

interface BookingEditDrawerProps {
  isOpen: boolean;
  booking: Booking | null;
  loading: boolean;
  employees: EmployeeOption[];
  onClose: () => void;
  onSubmit: (payload: {
    bookingId: number;
    bookingDate: string;
    bookingTime: string;
    status: BookingStatus;
    employeeId?: number;
    lineItems: LineItem[];
  }) => void;
  onDelete: (bookingId: number) => void;
}

const statusOptions: BookingStatus[] = [
  "Booked",
  "Pending",
  "Completed",
  "In Progress",
  "No Show",
  "Cancelled",
];

const BookingEditDrawer: React.FC<BookingEditDrawerProps> = ({
  isOpen,
  booking,
  loading,
  employees,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [status, setStatus] = useState<BookingStatus>("Booked");
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  // Services and Products state
  const [services, setServices] = useState<BusinessService[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // Fetch services and products when drawer opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchServices = async () => {
      setServicesLoading(true);
      try {
        const data = await getBusinessServices();
        setServices(data.filter((s) => s.status === "active"));
      } catch (error) {
        console.error("Failed to fetch services", error);
      } finally {
        setServicesLoading(false);
      }
    };

    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const data = await getAdminProducts();
        // Show all products in dropdown; we can visually indicate stock later if needed
        setProducts(data);
        console.log("Booking edit: loaded products for dropdown:", data);
      } catch (error) {
        console.error("Failed to fetch products", error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchServices();
    fetchProducts();
  }, [isOpen]);

  useEffect(() => {
    if (!booking) return;

    const dateSource = booking.rawData?.date_time;
    let parsedDate = dateSource ? new Date(dateSource) : new Date(NaN);

    // Fallback to bookingTime if rawData is missing or invalid
    if (isNaN(parsedDate.getTime()) && booking.bookingTime) {
      // Try to parse the formatted time (e.g., "23 Dec 2025 10:42 PM")
      parsedDate = new Date(booking.bookingTime);
    }

    // Ultimate fallback to current date if all else fails
    if (isNaN(parsedDate.getTime())) {
      parsedDate = new Date();
    }

    setBookingDate(parsedDate.toISOString().split("T")[0]);
    setBookingTime(
      `${String(parsedDate.getHours()).padStart(2, "0")}:${String(
        parsedDate.getMinutes()
      ).padStart(2, "0")}`
    );
    setStatus(booking.status);

    const employeeSource =
      booking.rawData?.employee?.id ||
      booking.rawData?.employee_id ||
      booking.rawData?.staff?.id ||
      booking.rawData?.staff_id;
    setEmployeeId(
      typeof employeeSource === "number" ? employeeSource : undefined
    );

    const items = Array.isArray(booking.rawData?.items)
      ? (booking.rawData?.items as any[])
      : [];

    setLineItems(
      items.map((item, idx) => ({
        id: String(item.id ?? `line-${idx}`),
        name: item.name || `Line Item ${idx + 1}`,
        unitPrice: Number(item.unit_price || item.amount || 0),
        quantity: Number(item.qty || item.quantity || 1),
        type: item.type || "service",
        serviceId: item.service_id ? String(item.service_id) : undefined,
        productId: item.product_id ? String(item.product_id) : undefined,
      }))
    );
  }, [booking]);

  const totals = useMemo(() => {
    const serviceTotal = lineItems
      .filter((item) => item.type === "service")
      .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    const productTotal = lineItems
      .filter((item) => item.type !== "service")
      .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    return {
      serviceTotal,
      productTotal,
      total: serviceTotal + productTotal,
    };
  }, [lineItems]);

  const updateQuantity = (lineId: string, delta: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineId
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    );
  };

  const removeLineItem = (lineId: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== lineId));
  };

  const addService = (service: BusinessService) => {
    const price = service.discountPrice || service.price;
    const newItem: LineItem = {
      id: `service-${service.id}-${Date.now()}`,
      name: service.name,
      unitPrice: price,
      quantity: 1,
      type: "service",
      serviceId: service.id,
    };
    setLineItems((prev) => [...prev, newItem]);
    setShowServiceDropdown(false);
    setServiceSearchTerm("");
  };

  const addProduct = (product: Product) => {
    const price = product.discountedPrice || product.price;
    const newItem: LineItem = {
      id: `product-${product.id}-${Date.now()}`,
      name: product.name,
      unitPrice: price,
      quantity: 1,
      type: "product",
      productId: product.id,
    };
    setLineItems((prev) => [...prev, newItem]);
    setShowProductDropdown(false);
    setProductSearchTerm("");
  };

  const filteredServices = useMemo(() => {
    if (!serviceSearchTerm) return services;
    const term = serviceSearchTerm.toLowerCase();
    return services.filter((s) => s.name.toLowerCase().includes(term));
  }, [services, serviceSearchTerm]);

  const filteredProducts = useMemo(() => {
    if (!productSearchTerm) return products;
    const term = productSearchTerm.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(term));
  }, [products, productSearchTerm]);

  const handleSubmit = () => {
    if (!booking) return;
    onSubmit({
      bookingId: booking.id,
      bookingDate,
      bookingTime,
      status,
      employeeId,
      lineItems,
    });
  };

  const { user } = useAuth();
  const authCurrency =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "£";

  const currency =
    booking?.rawData?.company?.currency?.currency_symbol ||
    booking?.rawData?.company?.currency?.currency_code ||
    authCurrency;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".service-dropdown-container") &&
        !target.closest(".product-dropdown-container")
      ) {
        setShowServiceDropdown(false);
        setShowProductDropdown(false);
      }
    };

    if (showServiceDropdown || showProductDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showServiceDropdown, showProductDropdown]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-5xl bg-white shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {isOpen && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Close edit booking drawer"
            className="fixed lg:-left-12 top-4 lg:top-9 right-4 lg:right-auto w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center text-[#885ABB] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
            onClick={onClose}
          >
            <X
              strokeWidth={3}
              size={20}
              className="group-hover:rotate-90 transition-transform duration-300"
            />
          </motion.button>
        )}
        <div className="h-full flex flex-col">
          <div className="border-b border-gray-100 px-4 sm:px-8 py-6">
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
              Edit Booking
            </p>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#402B69]">
              {booking?.customerName || "Loading..."}
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 space-y-6">
            {loading && (
              <div className="flex h-full items-center justify-center">
                <GlobalLoader
                  initialPageLoad={false}
                  compensateSidebar={false}
                  description="Loading booking details..."
                />
              </div>
            )}

            {!loading && booking && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl border border-[#C4A6E1] p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-purple-50 text-[#885ABB] flex items-center justify-center text-xl font-semibold">
                        {booking.customerName.charAt(0)}
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-[#402B69]">
                          {booking.customerName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {booking.rawData?.customer?.email || "—"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(booking.rawData?.customer as any)?.phone ||
                            (booking.rawData?.customer as any)?.phone_number ||
                            "—"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Booking Date
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full mt-1 justify-between text-left font-normal  shadow-none border-[#C4A6E1] bg-white hover:bg-white focus:ring-2 focus:ring-[#885ABB]",
                                !bookingDate && "text-muted-foreground"
                              )}
                            >
                              <span>
                                {bookingDate && isValid(parseISO(bookingDate))
                                  ? format(parseISO(bookingDate), "dd MMM yyyy")
                                  : "Select date"}
                              </span>
                              <Calendar className="h-4 w-4 text-[#885ABB]" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <DateCalendar
                              selectedDate={
                                bookingDate && isValid(parseISO(bookingDate))
                                  ? parseISO(bookingDate)
                                  : new Date()
                              }
                              onSelectDate={(date) => {
                                if (date) {
                                  setBookingDate(format(date, "yyyy-MM-dd"));
                                  // Close popover logic would need state if we want to auto control it,
                                  // but PopoverTrigger handles basic toggle.
                                  // The User asked to use DateCalendar, this matches the request.
                                }
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Booking Time
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full mt-1 justify-between text-left font-normal  shadow-none border-[#C4A6E1] bg-white hover:bg-white focus:ring-2 focus:ring-[#885ABB]",
                                !bookingTime && "text-muted-foreground"
                              )}
                            >
                              <span>
                                {bookingTime
                                  ? format(
                                    parse(bookingTime, "HH:mm", new Date()),
                                    "h:mm aa"
                                  )
                                  : "Select time"}
                              </span>
                              <Clock className="h-4 w-4 text-[#885ABB]" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-4" align="start">
                            <TimeSpinner
                              date={
                                bookingTime
                                  ? parse(bookingTime, "HH:mm", new Date())
                                  : new Date()
                              }
                              onChange={(time: string) => setBookingTime(time)}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Booking Status
                        </label>
                        <div className="mt-1">
                          <Select
                            value={status}
                            onValueChange={(value) =>
                              setStatus(value as BookingStatus)
                            }
                          >
                            <SelectTrigger className="w-full border-[#C4A6E1] bg-white focus:ring-2 focus:ring-[#885ABB]">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {statusOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Employee
                        </label>
                        <div className="mt-1">
                          <Select
                            value={employeeId?.toString() ?? "unassigned"}
                            onValueChange={(value) =>
                              setEmployeeId(
                                value === "unassigned"
                                  ? undefined
                                  : Number(value)
                              )
                            }
                          >
                            <SelectTrigger className="w-full border-[#C4A6E1] bg-white focus:ring-2 focus:ring-[#885ABB]">
                              <SelectValue placeholder="Select Employee" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              <SelectItem value="unassigned">
                                Select Employee
                              </SelectItem>
                              {employees.map((employee) => (
                                <SelectItem
                                  key={employee.id}
                                  value={employee.id.toString()}
                                >
                                  {employee.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Services & Products section */}
                  {/* NOTE: no overflow-hidden here so dropdowns can render outside the card */}
                  <div className="border border-[#C4A6E1] rounded-2xl shadow-sm">
                    <div className="bg-[#FCFCFD] px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-[#402B69] uppercase tracking-wide">
                        Services & Products
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 relative">
                        <div className="relative service-dropdown-container flex-1 sm:flex-none">
                          <button
                            onClick={() => {
                              setShowServiceDropdown(!showServiceDropdown);
                              setShowProductDropdown(false);
                            }}
                            className="w-full sm:w-auto px-3 py-1.5 text-sm font-semibold text-white bg-[#885ABB] rounded-md hover:bg-[#6f47a2] transition flex items-center justify-center gap-1"
                          >
                            + Add Service
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          {showServiceDropdown && (
                            <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-[calc(100vw-3rem)] sm:w-80 bg-white border border-[#C4A6E1] rounded-lg shadow-lg z-[60] max-h-96 overflow-hidden">
                              <div className="p-2 border-b border-gray-200">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder="Search services..."
                                    value={serviceSearchTerm}
                                    onChange={(e) =>
                                      setServiceSearchTerm(e.target.value)
                                    }
                                    className="w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {servicesLoading ? (
                                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                                    Loading services...
                                  </div>
                                ) : filteredServices.length === 0 ? (
                                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                                    No services found
                                  </div>
                                ) : (
                                  filteredServices.map((service) => (
                                    <button
                                      key={service.id}
                                      onClick={() => addService(service)}
                                      className="w-full text-left px-4 py-3 hover:bg-purple-50 transition border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="font-semibold text-[#402B69]">
                                        {service.name}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {currency}
                                        {service.discountPrice ||
                                          service.price}{" "}
                                        • {service.durationLabel}
                                      </div>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="relative product-dropdown-container flex-1 sm:flex-none">
                          <button
                            onClick={() => {
                              setShowProductDropdown(!showProductDropdown);
                              setShowServiceDropdown(false);
                            }}
                            className="w-full sm:w-auto px-3 py-1.5 text-sm font-semibold text-[#885ABB] border border-[#885ABB] rounded-md hover:bg-purple-50 transition flex items-center justify-center gap-1"
                          >
                            + Add Product
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          {showProductDropdown && (
                            <div className="absolute right-0 mt-2 w-[calc(100vw-3rem)] sm:w-80 bg-white border border-[#C4A6E1] rounded-lg shadow-lg z-[60] max-h-96 overflow-hidden">
                              <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={productSearchTerm}
                                    onChange={(e) =>
                                      setProductSearchTerm(e.target.value)
                                    }
                                    className="w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {productsLoading ? (
                                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                                    Loading products...
                                  </div>
                                ) : filteredProducts.length === 0 ? (
                                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                                    No products found
                                  </div>
                                ) : (
                                  filteredProducts.map((product) => (
                                    <button
                                      key={product.id}
                                      onClick={() => addProduct(product)}
                                      className="w-full text-left px-4 py-3 hover:bg-purple-50 transition border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="font-semibold text-[#402B69]">
                                        {product.name}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {currency}
                                        {product.discountedPrice ||
                                          product.price}{" "}
                                        • Stock: {product.stock || 0}
                                      </div>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {lineItems.length === 0 && (
                        <div className="text-sm text-gray-500 px-6 py-8 text-center">
                          No items added to this booking yet.
                        </div>
                      )}
                      {lineItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex flex-col sm:grid sm:grid-cols-[2fr_1fr_1fr_1fr_auto] sm:items-center px-4 sm:px-6 py-4 text-sm gap-3 sm:gap-2"
                        >
                          <div className="text-gray-800 font-semibold break-words">
                            {item.name}
                            <span className="ml-2 text-xs text-gray-500 font-normal">
                              ({item.type === "service" ? "Service" : "Product"}
                              )
                            </span>
                          </div>

                          <div className="flex sm:contents items-center justify-between">
                            <div className="text-gray-500 sm:hidden">
                              Price:
                            </div>
                            <div className="text-gray-700 font-medium sm:font-normal">
                              {currency}
                              {item.unitPrice.toFixed(2)}
                            </div>
                          </div>

                          <div className="flex sm:contents items-center justify-between">
                            <div className="text-gray-500 sm:hidden">
                              Quantity:
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                className="w-8 h-8 rounded-full border border-[#885ABB] text-[#885ABB] flex items-center justify-center hover:bg-purple-50 transition"
                                onClick={() => updateQuantity(item.id, -1)}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-6 text-center font-semibold text-gray-800">
                                {item.quantity}
                              </span>
                              <button
                                className="w-8 h-8 rounded-full border border-[#885ABB] text-[#885ABB] flex items-center justify-center hover:bg-purple-50 transition"
                                onClick={() => updateQuantity(item.id, 1)}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div className="flex sm:contents items-center justify-between border-t border-gray-50 pt-3 sm:pt-0 sm:border-0">
                            <div className="text-gray-500 sm:hidden">
                              Total:
                            </div>
                            <div className="text-right font-bold text-[#885ABB] sm:text-gray-900 sm:font-semibold">
                              {currency}
                              {(item.unitPrice * item.quantity).toFixed(2)}
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <button
                              onClick={() => removeLineItem(item.id)}
                              className="w-10 h-10 sm:w-8 sm:h-8 rounded-full border border-red-300 text-red-600 flex items-center justify-center hover:bg-red-50 transition shadow-sm"
                              title="Remove item"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border border-[#C4A6E1] rounded-2xl p-6 shadow-sm">
                    <h3 className="text-sm font-semibold text-[#402B69] uppercase tracking-wide mb-4">
                      Payment Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Service Total</span>
                        <span className="font-semibold text-gray-800">
                          {currency}
                          {totals.serviceTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Product Total</span>
                        <span className="font-semibold text-gray-800">
                          {currency}
                          {totals.productTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t border-dashed border-gray-200 pt-3">
                        <span className="text-gray-600 font-semibold">
                          Total Amount
                        </span>
                        <span className="text-xl font-bold text-[#885ABB]">
                          {currency}
                          {totals.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!loading && booking && (
            <div className="px-4 sm:px-8 pb-6 bg-white border-t border-gray-100 pt-4 mt-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => onDelete(booking.id)}
                  className="w-full px-4 py-3 sm:py-4 bg-[#d9463b] text-white rounded-xl text-sm font-bold uppercase tracking-wider hover:bg-[#bb392f] transition shadow-md"
                >
                  Delete Booking
                </button>
                <button
                  onClick={handleSubmit}
                  className="w-full px-4 py-3 sm:py-4 bg-[#885ABB] text-white rounded-xl text-sm font-bold uppercase tracking-wider hover:bg-[#6f47a2] transition shadow-md"
                >
                  Submit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BookingEditDrawer;
